import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/permissions/my');
      setPermissions(res.data.permissions || []);
    } catch { setPermissions([]); }
  }, []);

  const logout = useCallback(async () => {
    // Clear local state IMMEDIATELY so any React re-render after this call
    // (including the navigate('/login') that usually follows) sees user=null.
    // This avoids the "login flash → redirect back to app" bug caused by the
    // brief window during which the API /auth/logout call is in flight.
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('google_auth_pending');
    setUser(null);
    setTenant(null);
    setPermissions([]);
    setNeedsPasswordChange(false);

    // Fire-and-forget server-side blacklist (don't await — we've already logged out locally)
    if (token) {
      api.post('/auth/logout').catch(() => { /* best-effort */ });
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      api.get('/auth/me').then(async (res) => {
        setUser(res.data.user);
        setTenant(res.data.tenant);
        // Fetch permissions after login
        try {
          const permRes = await api.get('/permissions/my');
          setPermissions(permRes.data.permissions || []);
        } catch { /* fallback */ }
      }).catch(() => {
        logout();
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [logout]);

  const login = async (email, password, rememberMe = true) => {
    const res = await api.post('/auth/login', { email, password });
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      return res.data; // Return to caller to show 2FA step
    }
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', res.data.token);
    setUser(res.data.user);
    setTenant(res.data.tenant);
    if (res.data.needs_password_change) {
      setNeedsPasswordChange(true);
    }
    await fetchPermissions();
    return res.data;
  };

  const createTenant = async (data) => {
    const res = await api.post('/tenant/create', data);
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    setTenant(res.data.tenant);
    return res.data;
  };

  const registerWithCode = async (data) => {
    const res = await api.post('/auth/register', data);
    // If the backend requires email verification, do NOT auto-login.
    // Caller is responsible for showing the "check your email" screen.
    if (res.data?.email_verification_required) {
      return res.data;
    }
    if (res.data?.token) {
      localStorage.setItem('token', res.data.token);
      setUser(res.data.user);
      setTenant(res.data.tenant);
    }
    return res.data;
  };

  const loginWithGoogle = useCallback((data) => {
    localStorage.setItem('token', data.token);
    setUser(data.user);
    setTenant(data.tenant);
  }, []);

  const loginWithToken = useCallback(async (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    // Fetch tenant
    try {
      const tRes = await api.get('/tenant/current');
      setTenant(tRes.data);
    } catch { /* ignore */ }
    await fetchPermissions();
  }, [fetchPermissions]);

  const startGoogleAuth = (mode, extraData = {}) => {
    // Google OAuth via direct provider — configure REACT_APP_GOOGLE_CLIENT_ID
    localStorage.setItem('google_auth_pending', JSON.stringify({ mode, ...extraData }));
  };

  const startAppleAuth = (mode) => {
    // Apple Sign-In requires Apple Developer credentials to be configured
    // Once configured, this will redirect to Apple's auth page
    const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    if (!appleClientId) {
      return null; // Not configured yet
    }
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/apple/callback');
    const scope = 'name email';
    const state = JSON.stringify({ mode });
    window.location.href = `https://appleid.apple.com/auth/authorize?client_id=${appleClientId}&redirect_uri=${redirectUri}&response_type=code id_token&scope=${scope}&response_mode=form_post&state=${encodeURIComponent(state)}`;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const completePasswordChange = () => {
    setNeedsPasswordChange(false);
  };

  const hasPermission = useCallback((perm) => {
    if (['admin', 'superadmin', 'vice_superadmin'].includes(user?.role)) return true;
    return permissions.includes(perm);
  }, [permissions, user?.role]);

  return (
    <AuthContext.Provider value={{
      user, tenant, permissions, hasPermission, login, createTenant, registerWithCode,
      loginWithGoogle, loginWithToken, startGoogleAuth, startAppleAuth, logout, loading, updateUser,
      needsPasswordChange, completePasswordChange, fetchPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};
